import React, { useEffect, useState } from 'react';
import Button from 'antd/lib/button';
import ConfigProvider from 'antd/lib/config-provider';
import Dropdown from 'antd/lib/dropdown';
import Empty from 'antd/lib/empty';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { getRecentIncidents, getEventsList, snoozeAlert } from 'parity-network-requests';
import { config } from '../../config';
import { CustomSnoozeModal } from '../CustomSnoozeModal';

// ICONS
import buildingProfile from './assets/building-profile.svg';
import arrowWhite from './assets/arrow-white.svg';
import arrowDark from './assets/arrow-dark.svg';
import snoozeIconBlue from '../../icons/snooze-blue.svg';
import snoozeDropdownArrow from '../../icons/arrow-down-blue.svg';
import { CheckmarkIcon } from './components/CheckmarkIcon';
import { SnoozeIcon } from './components/SnoozeIcon';

// IMAGES
import waitingIcon from '../../images/waiting.png';
import noAlerts from '../../images/no-alerts.png';
import noSnoozedAlertsLight from '../../images/no-snoozed-alerts-light.png';
import noSnoozedAlertsDark from '../../images/no-snoozed-alerts-dark.png';

import './MultiBuildingModal.less';

const { REACT_APP_BUILD_ENV } = config;

export const MultiBuildingModal = (props) => {
  const { buildingInfo, building, onCancel, theme, buildings } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [alertsSetup, setAlertsSetup] = useState(false);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [alertsCategory, setAlertsCategory] = useState('New');
  const [nonSnoozedAlerts, setNonSnoozedAlerts] = useState([]);
  const [snoozedAlerts, setSnoozedAlerts] = useState([]);
  const [snoozedAlert, setSnoozedAlert] = useState({});
  const [isCustomSnoozeModalOpen, setIsCustomSnoozeModalOpen] = useState(false);
  const history = useHistory();
  const {
    name,
    city: { name: cityName },
    country: { name: countryName },
    province: { name: provinceName },
    postalCode,
  } = buildingInfo;
  const postal = postalCode !== 'N/A' ? postalCode : '';

  const getBuildingPermissions = () => {
    const selectedBuildingObj = buildings.find((buildingObj) => buildingObj.identifier === building);

    return selectedBuildingObj.permissions;
  };

  useEffect(() => {
    async function fetchData() {
      const permissions = getBuildingPermissions();

      if (permissions.indexOf('alerts') !== -1) {
        const incidentsList = await getRecentIncidents(REACT_APP_BUILD_ENV, building);
        setAlertsSetup(incidentsList.alerts_setup);

        const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

        const sortedNonSnoozedActiveEvents = activeEventsFromApi.results.filter(
          (event) => event.snooze_context.is_snoozed === false,
        );
        const sortedSnoozedActiveEvents = activeEventsFromApi.results.filter(
          (event) => event.snooze_context.is_snoozed === true,
        );

        setActiveAlerts(activeEventsFromApi.results);
        setNonSnoozedAlerts(sortedNonSnoozedActiveEvents);
        setSnoozedAlerts(sortedSnoozedActiveEvents);
      }

      setIsLoading(false);
    }
    fetchData();
  }, [building]);

  const onSnoozeButtonPress = async (alert) => {
    const { alert_uid_hash } = alert;
    let response;

    if (alert.snooze_context.is_snoozed) {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 0);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been unsnoozed`,
          description: `You will resume receiving notifications.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be unsnoozed`,
          description: `We encountered an error while trying to unsnooze this alert. Please try again.`,
        });
      }
    } else {
      response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, 3);

      if (response === true) {
        // Handle success
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert has been snoozed`,
          description: `You will resume receiving notifications when you unsnooze it.`,
        });
      } else {
        notification.info({
          className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
          placement: 'topRight',
          duration: 5,
          icon: <CheckmarkIcon theme={theme} />,
          message: `This alert could not be snoozed`,
          description: `We encountered an error while trying to snooze this alert. Please try again.`,
        });
      }
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );

    setActiveAlerts(activeEventsFromApi.results);
    setNonSnoozedAlerts(nonSnoozedActiveEventsFromApi);
    setSnoozedAlerts(snoozedActiveEventsFromApi);
  };

  const submitSnoozeAlert = async (hours) => {
    const { alert_uid_hash } = snoozedAlert;

    setIsCustomSnoozeModalOpen(false);
    const response = await snoozeAlert(REACT_APP_BUILD_ENV, alert_uid_hash, hours);

    if (response === true) {
      // Handle success
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert has been snoozed`,
        description: `You will resume receiving notifications from it starting ${moment()
          .add(hours, 'hours')
          .format('LLL')}`,
      });
    } else {
      notification.info({
        className: `alerts-snooze-notification alerts-snooze-notification-${theme}`,
        placement: 'topRight',
        duration: 5,
        icon: <CheckmarkIcon theme={theme} />,
        message: `This alert could not be snoozed`,
        description: `We encountered an error while trying to snooze this alert. Please try again.`,
      });
    }

    const activeEventsFromApi = await getEventsList(REACT_APP_BUILD_ENV, building, { is_resolved: false });

    const nonSnoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === false,
    );
    const snoozedActiveEventsFromApi = activeEventsFromApi.results.filter(
      (event) => event.snooze_context.is_snoozed === true,
    );

    setActiveAlerts(activeEventsFromApi.results);
    setNonSnoozedAlerts(nonSnoozedActiveEventsFromApi);
    setSnoozedAlerts(snoozedActiveEventsFromApi);
  };

  const getAlertSeverityColour = (alert) => {
    if (alert.severity === 'high') return '#EA856F';
    if (alert.severity === 'medium') return '#DEC802';
    return '#796FEA';
  };

  const paginationProperties = {
    className: 'multi-building-modal-alerts-pagination',
    hideOnSinglePage: true,
    pageSize: 3,
    showSizeChanger: false,
    total: activeAlerts.length,
    showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} alerts`,
  };

  const renderNoNonSnoozedAlerts = () => {
    return (
      <Empty
        className="multi-building-modal-no-active-alerts-container"
        image={noAlerts}
        description={
          <div className="multi-building-modal-no-active-alerts-content">
            <strong>Good News! Nothing needs your attention right now</strong>
            <span>We will alert you when there are problems happening with your building</span>
          </div>
        }
      />
    );
  };

  const renderNoSnoozedAlerts = () => {
    return (
      <Empty
        className="multi-building-modal-no-snoozed-alerts-container"
        image={theme === 'light' ? noSnoozedAlertsLight : noSnoozedAlertsDark}
        description={
          <div className="multi-building-modal-no-snoozed-alerts-content">
            <strong>Alerts that have been snoozed will appear here</strong>
            <span>You can snooze your alerts when you want to temporarily pause e-mail and text notifications</span>
          </div>
        }
      />
    );
  };

  const snoozeMenu = (
    <Menu className={`alerts-snooze-menu app-dropdown-menu app-dropdown-menu-${theme}`}>
      <Menu.Item key="0" disabled>
        <div role="presentation">Snooze for...</div>
      </Menu.Item>
      <Menu.Item key="1">
        <div role="presentation" onClick={() => submitSnoozeAlert(3)}>
          3 hours
        </div>
      </Menu.Item>
      <Menu.Item key="2">
        <div role="presentation" onClick={() => submitSnoozeAlert(6)}>
          6 hours
        </div>
      </Menu.Item>
      <Menu.Item key="3">
        <div role="presentation" onClick={() => submitSnoozeAlert(12)}>
          12 hours
        </div>
      </Menu.Item>
      <Menu.Item key="4">
        <div role="presentation" onClick={() => submitSnoozeAlert(24)}>
          24 hours
        </div>
      </Menu.Item>
      <Menu.Item key="5">
        <div role="presentation" onClick={() => setIsCustomSnoozeModalOpen(true)}>
          Custom
        </div>
      </Menu.Item>
    </Menu>
  );

  const activeAlertsTableColumns = [
    {
      title: 'ALERTS',
      dataIndex: ['name', 'severity'],
      render: (text, alert) => {
        const incidentId = alert.incident_uid_hash;
        const eventId = alert.uid_hash;
        const severity = alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1);
        return (
          <div className="multi-building-modal-alert-container">
            <div
              className="multi-building-modal-alert-colour"
              style={{ borderLeftColor: getAlertSeverityColour(alert) }}
            />
            <div className="multi-building-modal-alert-contents">
              {alertsCategory === 'Snoozed' && (
                <div className="multi-building-modal-alert-snoozed-info">
                  <SnoozeIcon theme={theme} />
                  <span className="multi-building-modal-alert-snoozed-until">
                    {`Snoozed until ${moment(alert.snooze_context.snoozed_until).format('MMM D, YYYY (LT)')}`}
                  </span>
                </div>
              )}
              <span
                className="multi-building-modal-alert-title"
                onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                onKeyDown={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                role="link"
                tabIndex={0}
              >
                {alert.name}
              </span>
              <span className="multi-building-modal-alert-info">{`Alert Tier: ${severity}`}</span>
              <span className="multi-building-modal-alert-info">
                {`${moment(alert.last_seen_timestamp).fromNow()} • ${moment(alert.last_seen_timestamp).format(
                  'MMM D, YYYY (LT)',
                )}`}
              </span>
              <div className="multi-building-modal-alert-buttons">
                <Button
                  className="multi-building-modal-alert-view-button"
                  onClick={() => history.push(`/alerts/${building}?incident_id=${incidentId}&event_id=${eventId}`)}
                >
                  View Alert
                </Button>
                <div className="multi-building-modal-alert-snooze-container">
                  {alert.snooze_context.is_snoozed ? (
                    <Button
                      className={`snooze-component snooze-component-snoozed snooze-component-${theme}`}
                      onClick={() => onSnoozeButtonPress(alert)}
                    >
                      Unsnooze
                    </Button>
                  ) : (
                    <>
                      <Button
                        className={`snooze-component snooze-component-unsnoozed snooze-component-${theme}`}
                        onClick={() => onSnoozeButtonPress(alert)}
                      >
                        <img src={snoozeIconBlue} alt="snooze" />
                      </Button>
                      <Dropdown overlay={snoozeMenu} trigger={['click']} onClick={() => setSnoozedAlert(alert)}>
                        <Button className={`dropdown-component dropdown-component-${theme}`}>
                          <img src={snoozeDropdownArrow} alt="dropdown" />
                        </Button>
                      </Dropdown>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      className={`multi-building-modal multi-building-modal-${theme}`}
      style={{ position: 'absolute', right: 35, height: '81%', padding: '0' }}
      width="61rem"
      visible
      keyboard
      maskClosable
      closable
      centered
      title={
        <div className="modal-header">
          <div className="modal-header-building">
            <img src={buildingProfile} className="modal-building-pic" alt="building-icon" />
            <div className="modal-header-building-info">
              <div className="modal-header-building__main">{name}</div>
              <div className="modal-header-building__secondary">{`${cityName}, ${provinceName} ${postal} ${countryName}`}</div>
            </div>
          </div>
          <div className="modal-header__button-wrapper">
            <Button
              block
              size="medium"
              className="modal__button"
              onClick={() => {
                sessionStorage.setItem('selectedBuilding', building);
                history.push(`/home/${building}/`);
              }}
            >
              <div className="modal__buton-text">Manage building</div>
              {theme === 'light' ? (
                <img className="modal__buton-arrow" src={arrowWhite} alt="arrow-white" />
              ) : (
                <img className="modal__buton-arrow" src={arrowDark} alt="arrow-white" />
              )}
            </Button>
          </div>
        </div>
      }
      onCancel={onCancel}
      footer={null}
    >
      <div className="modal-content">
        {isLoading ? (
          <div className="modal--loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {alertsSetup ? (
              <>
                <div className="multi-building-modal-alerts-header">
                  <div className="multi-building-modal-alerts-title">{`Current Alerts (${activeAlerts.length})`}</div>
                  <div className="multi-building-modal-alerts-toggle-category">
                    <Radio.Group onChange={(e) => setAlertsCategory(e.target.value)} defaultValue="New">
                      <Radio.Button className="multi-building-modal-alerts-new-button" value="New">
                        {`New (${nonSnoozedAlerts.length})`}
                      </Radio.Button>
                      <Radio.Button className="multi-building-modal-alerts-snoozed-button" value="Snoozed">
                        {`Snoozed (${snoozedAlerts.length})`}
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                </div>
                {activeAlerts.length > 0 ? (
                  <ConfigProvider
                    renderEmpty={alertsCategory === 'New' ? renderNoNonSnoozedAlerts : renderNoSnoozedAlerts}
                  >
                    <Table
                      className="multi-building-modal-alerts-list"
                      columns={activeAlertsTableColumns}
                      dataSource={alertsCategory === 'New' ? [...nonSnoozedAlerts] : [...snoozedAlerts]}
                      pagination={paginationProperties}
                    />
                  </ConfigProvider>
                ) : (
                  <div className="multi-building-modal-no-alerts">
                    <div className="multi-building-modal-no-alerts-icon">
                      <CheckmarkIcon theme="green" />
                    </div>
                    <div className="multi-building-modal-no-alerts-content">
                      <div className="multi-building-modal-no-alerts-title">
                        <strong>Good news!</strong>
                        <span>Nothing needs your attention right now.</span>
                      </div>
                      <span>We will alert you when there are problems happening with your building!</span>
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}
        {getBuildingPermissions().indexOf('savings') !== -1 && (
          <div className="modal-savings-section">
            {isLoading ? (
              <div className="modal--loading">
                <Spin size="large" />
              </div>
            ) : (
              <>
                <div className="modal-savings-header">
                  <div>Savings</div>
                </div>
                <div className="modal-savings-waiting-section">
                  <img src={waitingIcon} alt="yearly waiting" className="modal-savings-waiting" />
                  <div className="modal-savings-text-main">We are collecting data right now</div>
                  <div className="modal-savings-text-secondary">
                    Your savings will appear after software implementation period is complete
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {isCustomSnoozeModalOpen && (
        <CustomSnoozeModal
          onCancel={() => setIsCustomSnoozeModalOpen(false)}
          onSubmit={submitSnoozeAlert}
          theme={theme}
        />
      )}
    </Modal>
  );
};

MultiBuildingModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  building: PropTypes.string.isRequired,
};
